/**
 * Converts a given number of bytes into a human-readable string with appropriate units.
 *
 * @param bytes - The number of bytes to be converted.
 * @returns A string representing the size in appropriate units (B, KB, MB, GB, TB).
 */
export const convertFromBytes = (bytes: number) => {
    const units = ['B', 'KB', 'MB', 'GB', 'TB']
    let unitIndex = 0
    while (bytes >= 1024) {
        bytes /= 1024
        unitIndex++
    }
    return `${bytes.toFixed(0)}${units[unitIndex]}`
}

/**
 * Checks if a given file's type matches any of the accepted types.
 *
 * @param file - The file to check.
 * @param accept - A comma-separated string of accepted file types.
 *                 It can include MIME types (e.g., 'image/png'),
 *                 file extensions (e.g., '.png'), and wildcards (e.g., 'image/*').
 * @returns `true` if the file type matches any of the accepted types, otherwise `false`.
 */
export const checkFileType = (file: File, accept: string) => {
    const types = accept.split(',').map(x => x.trim())

    const checkForWildcard = types
        .filter(type => type.includes('*'))
        .some(type => new RegExp(type.replace('*', '.')).test(file.type))

    // možná přidat ještě trim?
    const checkExtension = types
        .filter(type => type.includes('.'))
        .includes(`.${file.name.split('.').pop()?.toLowerCase()}`)

    return types.includes(file.type) || checkForWildcard || checkExtension
}

interface CanvasSettings {
    /**
     * The maximum height of the image. If the image exceeds this height, it will be scaled down.
     */
    maxHeight?: number
    /**
     * The maximum width of the image. If the image exceeds this width, it will be scaled down.
     */
    maxWidth?: number
}

/**
 * Draws an image onto a canvas element, resizing it to fit within the specified maximum width and height while maintaining the aspect ratio.
 *
 * @param image - The image element to be drawn on the canvas.
 * @param settings - An object containing the maximum width and height for the canvas.
 * @param settings.maxHeight - The maximum height for the canvas.
 * @param settings.maxWidth - The maximum width for the canvas.
 * @param canvas - The canvas element to draw the image on. If not provided, a new canvas element will be created.
 * @returns The canvas element with the drawn image.
 */
export const drawImageInCanvas = (image: HTMLImageElement, { maxHeight, maxWidth }: CanvasSettings, canvas: HTMLCanvasElement = document.createElement('canvas')) => {
    const aspectRatio = image.width / image.height

    if (maxWidth && image.width > maxWidth) {
        image.width = maxWidth
        image.height = maxWidth / aspectRatio
    }

    if (maxHeight && image.height > maxHeight) {
        image.height = maxHeight
        image.width = maxHeight * aspectRatio
    }

    canvas.width = image.width
    canvas.height = image.height
    const context = canvas.getContext('2d')

    if (context) {
        // Fill the canvas with a white background
        context.fillStyle = 'white'
        context.fillRect(0, 0, canvas.width, canvas.height)
        context.drawImage(image, 0, 0, image.width, image.height)
    }

    return canvas
}

/**
 * Converts a given File object to an HTMLImageElement.
 *
 * @param file - The File or Blob object to be converted.
 * @returns A promise that resolves to an HTMLImageElement.
 */
export const convertFileToImage = async (file: File | Blob) => {
    return new Promise<HTMLImageElement>((resolve, reject) => {
        const reader = new FileReader()
        reader.addEventListener('load', () => {
            const image = new Image()
            image.addEventListener('load', () => resolve(image))
            /** @todo: zjistit, zda je to bezpečný přepsat na addEventListener */
            image.onerror = reject
            image.src = reader.result as string
        })
        /** @todo: zjistit, zda je to bezpečný přepsat na addEventListener */
        reader.onerror = reject
        reader.readAsDataURL(file)
    })
}

interface ConvertToJpegSettings extends CanvasSettings {
    /**
     * The quality of the JPEG image (between 0 and 1).
     * @default 0.8
     */
    quality?: number
}

/**
 * Converts an HTMLImageElement to a JPEG data URL with optional settings for quality, max width, and max height.
 *
 * @returns A data URL containing the JPEG representation of the image.
 */
export const convertImageToJpeg = async (image: HTMLImageElement, settings: ConvertToJpegSettings = {}) => {
    // set default values
    const { quality = 1 } = settings
    const canvas = drawImageInCanvas(image, settings)

    return canvas.toDataURL('image/jpeg', quality)
}

/**
 * Converts a given file to a JPEG image.
 *
 * @param file - The file to be converted.
 * @param settings - Optional settings for the conversion process.
 * @returns A promise that resolves to the converted JPEG image.
 */
export const convertFileToJpeg = async (file: File | Blob, settings: ConvertToJpegSettings = {}) => {
    const image = await convertFileToImage(file)
    return convertImageToJpeg(image, settings)
}

/**
 * Converts an HTMLImageElement to a JPEG file.
 *
 * @param image - The HTMLImageElement to be converted.
 * @param settings - Optional settings for the conversion process.
 * @returns A promise that resolves to a File object containing the JPEG image.
 * @throws An error if the conversion fails.
 */
export const convertJpegToFile = async (image: HTMLImageElement, settings: ConvertToJpegSettings = {}) => {
    const canvas = drawImageInCanvas(image, settings)

    return new Promise<File>((resolve, reject) => {
        canvas.toBlob((blob) => {
            if (blob) {
                const file = new File([blob], 'logo.jpeg', { type: 'image/jpeg' })
                resolve(file)
            } else {
                reject(new Error('Failed to convert image to JPEG'))
            }
        }, 'image/jpeg', settings.quality)
    })
}

/**
 * Converts a given file or blob to a JPEG file.
 *
 * @param file - The file or blob to be converted.
 * @param settings - Optional settings for the conversion process.
 * @returns A promise that resolves to the converted JPG file.
 */
export const convertFileToJpegToFile = async (file: File | Blob, settings: ConvertToJpegSettings = {}) => {
    const image = await convertFileToImage(file)
    return convertJpegToFile(image, settings)
}
